import { useEffect, useRef, useState } from "react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { WHATSAPP_CHAT_URL } from "@/lib/brand";

/**
 * Floating WhatsApp chat button — rendered once in Layout so it appears on every page.
 * Uses WhatsApp brand green (#25D366) — the one deliberate exception to the site palette,
 * because that green is what makes the glyph instantly recognisable.
 *
 * Hides itself cleanly when the site footer scrolls into view, so it never obscures
 * the disclaimer blocks, grievance line, or copyright line at the bottom of the page.
 */
export default function FloatingWhatsApp() {
  const [hidden, setHidden] = useState(false);
  const rafRef = useRef(0);

  useEffect(() => {
    const check = () => {
      const footer = document.querySelector('[data-testid="site-footer"]');
      if (!footer) return;
      const rect = footer.getBoundingClientRect();
      // Hide once the footer starts entering the viewport (so bottom disclaimers stay clear)
      setHidden(rect.top < window.innerHeight - 40);
    };
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = 0;
        check();
      });
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      aria-hidden={hidden || undefined}
      className={
        "fixed z-40 right-5 bottom-5 md:right-6 md:bottom-6 " +
        "transition-all duration-300 ease-out " +
        (hidden
          ? "opacity-0 pointer-events-none translate-y-3"
          : "opacity-100 translate-y-0")
      }
      style={{
        paddingRight: "max(0px, env(safe-area-inset-right))",
        paddingBottom: "max(0px, env(safe-area-inset-bottom))",
      }}
    >
      <a
        href={WHATSAPP_CHAT_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with Reliserve Wealth on WhatsApp"
        data-testid="floating-whatsapp"
        tabIndex={hidden ? -1 : 0}
        className="
          group relative inline-flex items-center justify-center
          h-14 w-14 md:h-[60px] md:w-[60px]
          rounded-full
          text-white
          shadow-[0_10px_28px_-6px_rgba(0,0,0,0.35),0_4px_10px_-2px_rgba(0,0,0,0.25)]
          ring-1 ring-white/10
          transition-transform duration-200 ease-out
          hover:scale-[1.06] active:scale-[0.98]
          focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/40
          motion-safe:animate-[wa-pulse_2.6s_ease-in-out_infinite]
        "
        style={{ backgroundColor: "#25D366", minWidth: 44, minHeight: 44 }}
      >
        <WhatsAppIcon size={28} />
        {/* Tooltip (desktop only, revealed on hover/focus) */}
        <span
          role="tooltip"
          className="
            hidden md:block
            pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-3
            whitespace-nowrap rounded-md px-3 py-1.5 text-[12px] font-medium
            bg-ink text-paper shadow-lg
            opacity-0 translate-x-1 transition-all duration-150
            group-hover:opacity-100 group-hover:translate-x-0
            group-focus-visible:opacity-100 group-focus-visible:translate-x-0
          "
        >
          Chat with us on WhatsApp
        </span>
      </a>
      {/* Gentle pulse (disabled automatically under prefers-reduced-motion via motion-safe:) */}
      <style>{`
        @keyframes wa-pulse {
          0%, 100% { box-shadow: 0 10px 28px -6px rgba(0,0,0,0.35), 0 4px 10px -2px rgba(0,0,0,0.25), 0 0 0 0 rgba(37,211,102,0.45); }
          50%      { box-shadow: 0 10px 28px -6px rgba(0,0,0,0.35), 0 4px 10px -2px rgba(0,0,0,0.25), 0 0 0 10px rgba(37,211,102,0); }
        }
      `}</style>
    </div>
  );
}
